import { useMemo } from 'react'

import { useApolloClient } from '@apollo/client'

import {
  approvedAtFilter,
  createCategoryAsyncFilter,
  createCurrencyAsyncFilter,
  createEmployeeIdsAsyncFilter,
  createLegalEntityIdsAsyncFilter,
  createProjectAsyncFilter,
  createReimbursableCurrencyAsyncFilter,
  createSubcategoryAsyncFilter,
  creationTypeFilter,
  documentTypeFilter,
  groupedFilter,
  internalReferenceFilter,
  pendingApprovalFilter,
  reimbursableFilter,
  reportDateFilter,
  spendingAlertTypeFilter,
  statusFilter,
} from './filters'

interface UseExpensesFiltersProps {
  companyId: number
  isGroupView?: boolean
}

/**
 * Filter types:
 * - Simple filters: Use factory functions, no async data fetching
 * - Async filters: Fetch data on-demand from GraphQL, cached by Apollo Client
 */
export const useExpensesFilters = ({ companyId, isGroupView }: UseExpensesFiltersProps) => {
  const apolloClient = useApolloClient()

  return useMemo(
    () => ({
      spendingAlertType: spendingAlertTypeFilter(), // alerts
      approvedAt: approvedAtFilter(), // approval date
      category: createCategoryAsyncFilter(apolloClient, companyId), // category
      reviewRequestAt: reportDateFilter(), // creation date
      currency: createCurrencyAsyncFilter(apolloClient, companyId), // currency
      documentType: documentTypeFilter(), // document type
      creationType: creationTypeFilter(), // expense type
      ...(!isGroupView ? { grouped: groupedFilter() } : {}), // grouped
      internalReference: internalReferenceFilter(), // internal reference
      employeeIds: createEmployeeIdsAsyncFilter(apolloClient, companyId), // owner
      legalEntityIds: createLegalEntityIdsAsyncFilter(apolloClient), // owner legal entity
      reimbursable: reimbursableFilter(), // payment
      pendingApproval: pendingApprovalFilter(), // pending approval
      project: createProjectAsyncFilter(apolloClient), // project
      reimbursableCurrency: createReimbursableCurrencyAsyncFilter(apolloClient, companyId), // reimbursable currency
      status: statusFilter(), // status
      subcategory: createSubcategoryAsyncFilter(apolloClient, companyId), // subcategory
    }),
    [apolloClient, companyId, isGroupView]
  )
}
